<template>
  <material-card-sba
    :size.sync="internalSize"
    :order.sync="internalOrder"
    :height.sync="internalHeight"
    :maxOrderValue="maxOrderValue"
    :isBenefitCardsWidget="false"
    color="accent"
    :widgetIcon="widgetIcon"
    :title="$t('components.vue_material_dashboard.items.recent_claims.title')"
  >
    <v-row
      v-if="!loading && claimsData?.length"
      no-gutters
      class="mx-6 mt-0 fill-height"
      justify="start"
    >
      <v-col>
        <!-- Patient Selector -->
        <v-select
          v-model="selectedDependantId"
          :items="patientOptions"
          item-text="label"
          item-value="id"
          :label="$t('components.new_claim.patient')"
          hide-details
          outlined
          dense
          class="mb-0 mb-md-2 pt-4"
        />

        <!-- Claim List -->
        <v-list two-line dense>
          <template v-for="claim in filteredClaims">
            <v-list-item :key="claim.claimId" class="pa-0">
              <v-list-item-content>
                <v-list-item-title
                  class="d-flex justify-space-between flex-wrap"
                >
                  <div class="pa-0 font-weight-bold accent--text">
                    {{ claim.submissionId }}
                  </div>
                  <div class="pa-0 font-weight-bold">
                    {{ claim.coverage }}
                  </div>
                </v-list-item-title>
                <v-list-item-subtitle
                  class="pa-0 d-flex flex-wrap justify-space-between"
                >
                  {{ $t("globals.date") }}:
                  <span class="font-weight-bold">{{
                    $helpers.formatDate(claim.date)
                  }}</span>
                  | {{ $t("globals.claimed") }}:
                  <span class="font-weight-bold"
                    >${{ claim.claimedAmount.toFixed(2) }}</span
                  >
                  | {{ $t("globals.paid") }}:
                  <span class="font-weight-bold">
                    ${{
                      claim.paidAmount != null
                        ? claim.paidAmount.toFixed(2)
                        : "0.00"
                    }}</span
                  >
                  | {{ $t("globals.status") }}:
                  <span
                    class="font-weight-bold"
                    :class="getTextClass(claim.status)"
                    >{{ claim.status }}</span
                  >
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-if="filteredClaims.length === 0"
              :key="claim.claimId + '-empty'"
            >
              <v-list-item-content>{{
                $t(
                  "components.vue_material_dashboard.items.recent_claims.no_claims"
                )
              }}</v-list-item-content>
            </v-list-item>
            <v-divider
              v-if="claim !== filteredClaims[filteredClaims.length - 1]"
              :key="claim.claimId + '-divider'"
            />
          </template>
        </v-list>
      </v-col>
    </v-row>
    <v-row
      v-if="!loading && claimsData?.length === 0"
      no-gutters
      :class="`fill-height justify-center mt-0 mb-12 mt-sm-12 pt-4pt-sm-12 pb-8 px-4`"
    >
      {{
        $t("components.vue_material_dashboard.items.my_recent_claim.no_claims")
      }}
    </v-row>

    <v-row
      v-if="
        !loading &&
        !useLegacyClaims &&
        !isDemoMockPlan &&
        !selectedPatient?.Balances
      "
      no-gutters
      :class="`
      fill-height
      justify-center
      mt-0
      mb-12
      mt-sm-12
      pt-4
      pt-sm-12
      pb-8
      px-4`"
    >
      {{
        $t(
          "components.vue_material_dashboard.items.used_benefits.history_coming_soon"
        )
      }}
    </v-row>

    <v-progress-circular
      v-if="loading"
      size="50"
      color="primary"
      class="loader"
      indeterminate
    ></v-progress-circular>
    <v-btn
      color="accent"
      class="visit-btn"
      @click="
        $router.push({
          name: 'claimsHistory',
          query: { name: selectedPatient?.FirstName },
        })
      "
      >{{ $t("globals.more") }}</v-btn
    >
  </material-card-sba>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import MaterialCardSba from "@/components/VueMaterialDashboard/material/MaterialCardSbaDashboard.vue";
export default {
  components: {
    MaterialCardSba,
  },
  props: {
    widgetId: String,
    widgetIcon: String,
    size: Number,
    order: Number,
    height: Number,
    maxOrderValue: Number,
  },
  data() {
    return {
      internalSize: undefined,
      internalOrder: undefined,
      internalHeight: undefined,
      selectedItem: undefined,
      memberBalances: { Patients: [] },
      selectedPatient: { Balances: [] },
      dependants: [],
      loading: false,
      claimsData: [],
      selectedDependantId: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("common", [
      "planBenefits",
      "useLegacyClaims",
      "isDemoMockPlan",
    ]),
    // Extract unique patient options
    patientOptions() {
      const patients = {};
      this.claimsData.forEach((submission) => {
        submission.Claims.forEach((claim) => {
          const id = claim.DependantId || claim.MemberId;
          const label = `${claim.FirstName} ${claim.LastName}`;
          if (!patients[id]) {
            patients[id] = { id, label };
          }
        });
      });
      return Object.values(patients);
    },

    // Flattened and sorted claim list
    allClaims() {
      return this.claimsData
        .flatMap((submission) =>
          submission.Claims.map((claim) => ({
            patientId: claim.DependantId || claim.MemberId,
            submissionId: submission.Identifier,
            claimId: claim.Id,
            coverage: claim.CurrentClaimData.PlanBenefit.Benefit.Name,
            date: submission.Timestamp,
            claimedAmount: claim.CurrentClaimData.ClaimedAmount || 0,
            paidAmount: claim.CurrentClaimData.PaidAmount,
            status: claim.CurrentClaimData.Status,
          }))
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    filteredClaims() {
      return this.allClaims
        .filter((c) => c.patientId === this.selectedDependantId)
        .slice(0, 3);
    },
  },
  watch: {
    size: {
      handler(v) {
        this.internalSize = v;
      },
    },
    internalSize: {
      handler(v) {
        this.$emit("update:size", v);
      },
    },
    height: {
      handler(v) {
        this.internalHeight = v;
      },
    },
    internalHeight: {
      handler(v) {
        this.$emit("update:height", v);
      },
    },
    order: {
      handler(v) {
        this.internalOrder = v;
      },
    },
    internalOrder: {
      handler(v) {
        this.$emit("update-order", v);
      },
    },
    watch: {
      selectedPatient: {
        handler(v) {
          if (v) {
            this.getPatientBalances();
          }
          console.log("selectedPatient: ", v);
        },
        deep: true,
      },
    },
  },
  async mounted() {
    if (this.size !== null && this.size !== undefined) {
      this.internalSize = this.size;
    }
    if (this.height !== null && this.height !== undefined) {
      this.internalHeight = this.height;
    }
    if (this.order !== null && this.order !== undefined) {
      this.internalOrder = this.order;
    }
    await this.loadData();
  },
  methods: {
    ...mapActions("data", [
      "getBenefitBalancesLegacy",
      "getBenefitBalancesDrawbridge",
      "getMostFrequentBenefitBalancesDrawbridge",
      "getMemberDependants",
      "searchDrawbridgeClaims",
    ]),
    async loadData() {
      try {
        this.loading = true;
        const data = (
          await this.searchDrawbridgeClaims({
            planIdentityId: this.user.plan,
            memberIdentityId: this.user.sub,
            // memberIdentityId: "8e442e9b-76d4-4706-9f96-79c334234734",
            page: 1,
            itemsPerPage: 2,
            sortBy: "Timestamp",
            sortDesc: true,
          })
        ).data;
        if (data && data.value?.length) {
          this.claimsData = data.value;
          this.selectedDependantId =
            this.claimsData[0].Claims[0].DependantId ||
            this.claimsData[0].Claims[0].MemberId;
          this.totalCount = data.totalCount;
        }
      } catch (error) {
        this.error = error;
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    getTextClass(status) {
      switch (status) {
        case "Approved":
          return "green--text";
        case "Paid":
          return "accent--text";
        case "Rejected":
          return "red--text";
        case "Pended":
          return "orange--text";
        default:
          return "grey--text";
      }
    },
    async getPatients() {
      try {
        this.loading = true;
        this.dependants = (
          await this.getMemberDependants({
            planIdentityId: this.user.plan,
          })
        ).data.sort((a, b) => {
          if (Date.parse(a.DateOfBirth) < Date.parse(b.DateOfBirth)) {
            return -1;
          } else if (Date.parse(a.DateOfBirth) === Date.parse(b.DateOfBirth)) {
            return 0;
          } else {
            return 1;
          }
        });
        if (this.dependants.length > 0) {
          this.selectedPatient = this.dependants[0];
          await this.getPatientBalances();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async setSelectedPatient(patient) {
      this.selectedPatient = patient;
      await this.getPatientBalances();
    },
    async getPatientBalances() {
      try {
        this.loading = true;
        const balances = (
          await this.getMostFrequentBenefitBalancesDrawbridge({
            planIdentityId: this.user.plan,
            memberIdentityId: this.user.sub,
            payload: {
              DependantId: this.selectedPatient?.DependantId,
            },
          })
        ).data.sort((a, b) =>
          a.LabelEn > b.LabelEn ? 1 : b.LabelEn > a.LabelEn ? -1 : 0
        );
        this.selectedPatient.Balances = balances;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
@media screen and (max-width: 1263px) {
  .benefit-title {
    font-size: 12px;
    font-weight: 700;
  }
  .balances {
    position: absolute;
    top: 25px;
    left: 22px;
  }
}
@media only screen and (min-width: 1264px) {
  .benefit-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
  }
  .balances {
    position: absolute;
    top: 40px;
    left: 33px;
  }
}
.benefit-subtitle {
  font-size: 12px;
  font-weight: 600;
}
.amount-title {
  font-size: 9px;
  font-weight: 700;
}
.amount {
  font-size: 11px;
  font-weight: 700;
}
.offset-36 {
  position: relative;
  top: -36px;
}
.balance-chart {
  position: relative;
}
.visit-btn {
  position: absolute;
  bottom: 24px;
  right: 24px;
}
.v-list-item {
  min-height: 20px;
}
.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.note {
  position: absolute;
  font-size: 12px;
  max-width: 400px;
  bottom: 20px;
  left: 0;
}
</style>
